'use client';

import { useTranslations } from 'next-intl';
import React, { useMemo, useState } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import Button from '@cineamo/ui/src/button/Button';
import { ButtonStyle } from '@cineamo/ui/src/button/ButtonStyle.enum';
import { IconChecked16 } from '@cineamo/ui/src/icons/ic-checked';
import ImageGallery from '@cineamo/ui/src/image/ImageGallery';
import Modal from '@cineamo/ui/src/modals/Modal';
import { seatColors } from '@cineamo/ui/src/seat-map/exampleData';
import type { Seat, SeatCategory } from '@cineamo/ui/src/seat-map/typedefs';

import { useCartProvider } from '../providers/cartProvider';

import { categorySurcharge, defaultPricesByCategory, roomSeatCategories } from './seat-showcase';

/** The photo of a seat category, or its color where the cinema has not added one. */
export const SeatCategoryVisual: React.FC<{ category: SeatCategory; color: string; className?: string }> = ({
    category,
    color,
    className
}) =>
    category.imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element -- a cinema's own upload, sized by its box
        <img
            src={category.imageUrl}
            alt=""
            className={cn('object-cover rounded-4', className)}
        />
    ) : (
        <div
            aria-hidden="true"
            className={cn('rounded-4', className)}
            style={{ background: color }}
        />
    );

/**
 * What a seat category offers, in full, as one modal for every entry point (legend, footer hint, cart line): a centered
 * dialog on desktop, a bottom sheet on phones. Escape, the backdrop and the x close it.
 */
const SeatCategoryShowcaseModal: React.FC<{
    category: SeatCategory;
    seats: Seat[];
    /** The room's categories, in the order the legend and the map color them by. */
    seatCategories: SeatCategory[];
    onClose: () => void;
    /** Given when the current selection can move to this category: one primary action under the content. */
    upgrade?: { total: string; perSeat?: string; isBusy?: boolean; onAccept: () => void };
}> = ({ category, seats, seatCategories, onClose, upgrade }) => {
    const t = useTranslations('tickets');
    const formatter = useFormat();
    const { currency, priceOptions } = useCartProvider();
    const [isPhotoOpen, setIsPhotoOpen] = useState(false);
    const surcharge = useMemo(
        () => categorySurcharge(category, roomSeatCategories(seats), defaultPricesByCategory(priceOptions)),
        [category, seats, priceOptions]
    );
    const index = seatCategories.findIndex((c) => c.id === category.id);
    const color = category.color ?? seatColors[Math.max(0, index) % seatColors.length];
    const benefits = category.benefits?.slice(0, 5) ?? [];
    return (
        // Rendered by the parent only while a category is open, so the modal is always open here.
        // eslint-disable-next-line cineamo/modal-conditional-render
        <Modal
            modalIsOpen
            onClose={onClose}
            title={category.name}
            // Phones: a sheet on the bottom edge (the modal's wrapper centers and pads by 9px).
            className="self-end -mb-[9px] rounded-b-none lg:mb-0 lg:max-w-[480px] lg:self-auto lg:rounded-b-8">
            <div className="flex flex-col gap-12 pb-4">
                {category.imageUrl && (
                    <>
                        <button
                            type="button"
                            onClick={() => setIsPhotoOpen(true)}
                            aria-label={t('seat-category-photo', { name: category.name })}
                            className="block aspect-video max-h-[240px] w-full shrink-0 cursor-zoom-in overflow-hidden rounded-4 outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-middle-color)]">
                            <SeatCategoryVisual
                                category={category}
                                color={color}
                                className="size-full"
                            />
                        </button>
                        <ImageGallery
                            images={[category.imageUrl]}
                            open={isPhotoOpen}
                            onClose={() => setIsPhotoOpen(false)}
                        />
                    </>
                )}
                {surcharge !== undefined && (
                    <p className="text-body-bold-14">
                        {t('seat-category-surcharge', {
                            price: formatter.number.formatCurrency(surcharge / 100, currency)
                        })}
                    </p>
                )}
                {category.description && (
                    // On phones the benefits are the argument and the sheet has to stay short.
                    <p className={cn('text-body-14', benefits.length >= 2 && 'hidden lg:block')}>
                        {category.description}
                    </p>
                )}
                {benefits.length > 0 && (
                    <ul className="flex flex-col gap-4">
                        {benefits.map((benefit) => (
                            <li
                                key={benefit}
                                className="flex items-start gap-6 text-body-14">
                                <IconChecked16
                                    className="mt-[2px] shrink-0 text-[var(--primary-middle-color)]"
                                    fill="currentColor"
                                />
                                {benefit}
                            </li>
                        ))}
                    </ul>
                )}
                {upgrade && (
                    <div className="flex flex-col gap-4">
                        <Button
                            style={ButtonStyle.default_on_white_bg}
                            disabled={upgrade.isBusy}
                            onClick={upgrade.onAccept}
                            className="min-h-[44px] w-full">
                            {t('seat-upgrade-accept', { name: category.name, total: upgrade.total })}
                        </Button>
                        {upgrade.perSeat && (
                            <p className="text-center text-body-12 opacity-70">
                                {t('seat-category-surcharge', { price: upgrade.perSeat })}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default SeatCategoryShowcaseModal;
